import React, { useEffect, useState, Fragment } from 'react'

import { string, node, func, bool } from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'
import InputField from 'Pods/Shared/NewInputField'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import { required } from 'Pods/Shared/Rules/Rules'

import MTCaptcha from 'Utils/MTCaptcha'
import { NOOP } from 'Utils/stableDefaults'

import VerifyNotice from './VerifyNotice'

const fieldValidations = [RuleRunner('email', 'Email', required)]

function GuestCheckout({
  email: initialEmail = '',
  checkbox = null,
  updateEmail = NOOP,
  captchaId = '',
  showVerificationNotice = false,
  useVerificationFlow = false,
  successPath = null,
}) {
  const [fields, setFields] = useState({
    email: initialEmail || '',
    email_confirmation: '', // this is a hidden field used as honey pot
    validationErrors: {},
    showErrors: false,
    isButtonDisabled: false,
    isNoticeVisible: showVerificationNotice,
  })
  const registrationCaptchaEnabled =
    window.gon.features && window.gon.features.cgcaptcha

  useEffect(() => {
    setFields({ ...fields, validationErrors: run(fields, fieldValidations) })
  }, [])

  function errorFor(field) {
    return fields.validationErrors[field] || ''
  }

  function handleInputChange(e) {
    const newFields = { ...fields, [e.target.name]: e.target.value }
    setFields({
      ...newFields,
      validationErrors: run(newFields, fieldValidations),
      isButtonDisabled: false,
    })
    updateEmail(newFields.email)
  }

  function handleSubmit(e) {
    e.preventDefault()

    try {
      setFields({ ...fields, showErrors: true, isButtonDisabled: true })
      if (Object.getOwnPropertyNames(fields.validationErrors).length !== 0) {
        return null
      }

      const data = {
        email: fields.email,
        email_confirmation: fields.email_confirmation, // this is a hidden field used as honey pot
        ...(registrationCaptchaEnabled && {
          MTCaptchaToken: window.mtcaptcha.getVerifiedToken(captchaId),
        }),
      }

      window.axios
        .post('/guests/sessions', data)
        .then(onSubmitSuccess)
        .catch(onSubmitFailure)
    } catch (error) {
      window.sendErrorToSentry(error, {
        tags: { section: 'Guest checkout' },
        extra: {
          msg: 'Guest checkout error',
        },
      })
    }
  }

  function onSubmitSuccess(response) {
    if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else {
      window.axios
        .put('/cg/checkout/track-login-to-payment-step', {
          email: fields.email,
        })
        .catch(() => {})

      if (useVerificationFlow) {
        setFields({ ...fields, isNoticeVisible: true })
      } else {
        window.location =
          successPath ||
          `/${window.location.pathname.split('/')[1]}/checkout/payment`
      }
    }
  }

  function onSubmitFailure(xhr) {
    if (registrationCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }

    setFields({ ...fields, isButtonDisabled: false })

    App.events.publish('app:notification:flash:error', [
      xhr.response.data.message || xhr.response.data.errors,
    ])
  }

  return (
    <div className="guest-user-checkout-container">
      {fields.isNoticeVisible ? (
        <VerifyNotice />
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <form
                onSubmit={handleSubmit}
                id="checkout-guest"
                action="/guests/sessions.json"
              >
                <div className="checkout-guest">
                  <div className="checkout-guest__email">
                    <InputField
                      label="Email"
                      id="guest_email"
                      name="email"
                      type="text"
                      errorText={errorFor('email')}
                      showError={fields.showErrors}
                      value={fields.email}
                      onChange={handleInputChange}
                    />
                    {/* honey pot field */}
                    <InputField
                      label="Email Confirmation"
                      id="guest_email_confirmation"
                      name="email_confirmation"
                      type="text"
                      errorText={errorFor('email')}
                      showError={fields.showErrors}
                      value={fields.email_confirmation}
                      onChange={handleInputChange}
                      autoComplete="off"
                      customClass="hidden"
                    />
                  </div>
                  {checkbox}
                  {registrationCaptchaEnabled && (
                    <MTCaptcha captchaId={captchaId} />
                  )}
                  <button
                    className="cgt-button cgt-button--primary cgt-button--icon cgt-button--width-expand"
                    disabled={fields.isButtonDisabled}
                  >
                    <span>Continue to payment</span>
                    <Icon type={['far', 'angle-right']} />
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="cgt-legacy-text-tiny">
                Your email address will only be used for sending you
                transactional information
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  )
}

GuestCheckout.propTypes = {
  email: string,
  checkbox: node,
  updateEmail: func,
  captchaId: string,
  showVerificationNotice: bool,
  useVerificationFlow: bool,
  successPath: string,
}

export default GuestCheckout
